<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import VendorPortalsAPI from 'dashboard/api/vendorPortals';

const blankForm = () => ({
  name: '',
  source_kind: 'vendor',
  provider_type: 'mawasim',
  base_url: '',
  auth_type: 'otp_email',
  username: '',
  login_email: '',
  otp_email: '',
  credential_secret: '',
  api_key: '',
  default_language: 'ar',
  supported_languages: ['ar', 'en'],
  status: 'enabled',
  margin_rules: {
    margin_type: 'percentage',
    margin_value: 8,
    minimum_margin: 50,
    competitor_undercut: 10,
    require_agent_approval: true,
  },
});

export default {
  components: {
    SettingsLayout,
    BaseSettingsHeader,
    NextButton,
  },
  data() {
    return {
      records: [],
      form: blankForm(),
      isLoading: false,
      isSaving: false,
      editingId: null,
    };
  },
  computed: {
    noRecords() {
      return !this.records.length;
    },
  },
  mounted() {
    this.fetchVendorPortals();
  },
  methods: {
    async fetchVendorPortals() {
      this.isLoading = true;
      try {
        const { data } = await VendorPortalsAPI.get();
        this.records = data.payload.vendor_portals || [];
      } catch (error) {
        useAlert(this.$t('VENDOR_PORTALS.ERRORS.LOAD'));
      } finally {
        this.isLoading = false;
      }
    },
    async saveVendorPortal() {
      this.isSaving = true;
      try {
        if (this.editingId) {
          await VendorPortalsAPI.update(this.editingId, {
            vendor_portal: this.form,
          });
        } else {
          await VendorPortalsAPI.create({ vendor_portal: this.form });
        }
        this.cancelEdit();
        await this.fetchVendorPortals();
        useAlert(this.$t('VENDOR_PORTALS.SUCCESS.SAVED'));
      } catch (error) {
        useAlert(this.$t('VENDOR_PORTALS.ERRORS.SAVE'));
      } finally {
        this.isSaving = false;
      }
    },
    editVendorPortal(portal) {
      this.editingId = portal.id;
      this.form = {
        ...blankForm(),
        ...portal,
        // Never pre-fill secrets - the API only tells us whether one is set
        // (has_credential_secret/has_api_key), not the value. Left blank,
        // the backend keeps the existing secret unchanged on update.
        credential_secret: '',
        api_key: '',
        margin_rules: {
          ...blankForm().margin_rules,
          ...(portal.margin_rules || {}),
        },
      };
    },
    cancelEdit() {
      this.editingId = null;
      this.form = blankForm();
    },
    async deleteVendorPortal(id) {
      try {
        await VendorPortalsAPI.delete(id);
        await this.fetchVendorPortals();
        useAlert(this.$t('VENDOR_PORTALS.SUCCESS.DELETED'));
      } catch (error) {
        useAlert(this.$t('VENDOR_PORTALS.ERRORS.DELETE'));
      }
    },
    async testVendorPortal(id) {
      try {
        await VendorPortalsAPI.testConnection(id);
        await this.fetchVendorPortals();
        useAlert(this.$t('VENDOR_PORTALS.SUCCESS.TESTED'));
      } catch (error) {
        useAlert(this.$t('VENDOR_PORTALS.ERRORS.TEST'));
      }
    },
    updateLanguages(value) {
      this.form.supported_languages = value
        .split(',')
        .map(lang => lang.trim())
        .filter(Boolean);
    },
    formatMargin(portal) {
      const value = portal.margin_rules?.margin_value || 0;
      const suffix =
        portal.margin_rules?.margin_type === 'percentage'
          ? this.$t('VENDOR_PORTALS.SYMBOLS.PERCENT')
          : '';
      return `${value}${suffix}`;
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('VENDOR_PORTALS.LOADING')"
    :no-records-message="$t('VENDOR_PORTALS.EMPTY')"
    :no-records-found="noRecords"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('VENDOR_PORTALS.TITLE')"
        :description="$t('VENDOR_PORTALS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <section class="min-w-0">
          <table
            v-if="records.length"
            class="min-w-full divide-y divide-n-weak"
          >
            <thead>
              <tr>
                <th
                  class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
                >
                  {{ $t('VENDOR_PORTALS.TABLE.NAME') }}
                </th>
                <th
                  class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
                >
                  {{ $t('VENDOR_PORTALS.TABLE.TYPE') }}
                </th>
                <th
                  class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
                >
                  {{ $t('VENDOR_PORTALS.TABLE.AUTH') }}
                </th>
                <th
                  class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
                >
                  {{ $t('VENDOR_PORTALS.TABLE.MARGIN') }}
                </th>
                <th
                  class="py-4 ltr:pr-4 rtl:pl-4 text-right font-semibold text-n-slate-11"
                >
                  {{ $t('VENDOR_PORTALS.TABLE.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak text-n-slate-12">
              <tr v-for="portal in records" :key="portal.id">
                <td class="py-4 ltr:pr-4 rtl:pl-4">
                  <div class="font-medium">{{ portal.name }}</div>
                  <div class="text-sm text-n-slate-10">
                    {{ portal.base_url || portal.provider_type }}
                  </div>
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4 capitalize">
                  {{ portal.source_kind }}
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4">{{ portal.auth_type }}</td>
                <td class="py-4 ltr:pr-4 rtl:pl-4">
                  {{ formatMargin(portal) }}
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4">
                  <div class="flex justify-end gap-2">
                    <NextButton
                      ghost
                      icon="i-lucide-plug-zap"
                      @click="testVendorPortal(portal.id)"
                    />
                    <NextButton
                      ghost
                      slate
                      icon="i-lucide-pencil"
                      @click="editVendorPortal(portal)"
                    />
                    <NextButton
                      ghost
                      slate
                      icon="i-lucide-trash-2"
                      @click="deleteVendorPortal(portal.id)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <aside class="space-y-4">
          <div class="rounded-lg border border-n-weak p-4">
            <h3 class="mb-4 text-base font-semibold text-n-slate-12">
              {{
                editingId
                  ? $t('VENDOR_PORTALS.FORM.EDIT_TITLE')
                  : $t('VENDOR_PORTALS.FORM.TITLE')
              }}
            </h3>

            <form class="space-y-3" @submit.prevent="saveVendorPortal">
              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.NAME')
                }}</span>
                <input
                  v-model="form.name"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                  required
                />
              </label>

              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.SOURCE_KIND')
                }}</span>
                <select
                  v-model="form.source_kind"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                >
                  <option value="vendor">
                    {{ $t('VENDOR_PORTALS.KINDS.VENDOR') }}
                  </option>
                  <option value="competitor">
                    {{ $t('VENDOR_PORTALS.KINDS.COMPETITOR') }}
                  </option>
                </select>
              </label>

              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.PROVIDER')
                }}</span>
                <input
                  v-model="form.provider_type"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                  required
                />
              </label>

              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.BASE_URL')
                }}</span>
                <input
                  v-model="form.base_url"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                />
              </label>

              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.AUTH_TYPE')
                }}</span>
                <select
                  v-model="form.auth_type"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                >
                  <option value="otp_email">
                    {{ $t('VENDOR_PORTALS.AUTH.OTP_EMAIL') }}
                  </option>
                  <option value="password">
                    {{ $t('VENDOR_PORTALS.AUTH.PASSWORD') }}
                  </option>
                  <option value="api_key">
                    {{ $t('VENDOR_PORTALS.AUTH.API_KEY') }}
                  </option>
                  <option value="no_auth">
                    {{ $t('VENDOR_PORTALS.AUTH.NO_AUTH') }}
                  </option>
                </select>
              </label>

              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.LOGIN_EMAIL')
                }}</span>
                <input
                  v-model="form.login_email"
                  type="email"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                />
              </label>

              <label v-if="form.auth_type === 'otp_email'" class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.OTP_EMAIL')
                }}</span>
                <input
                  v-model="form.otp_email"
                  type="email"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                />
              </label>

              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.SECRET')
                }}</span>
                <input
                  v-model="form.credential_secret"
                  type="password"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                  autocomplete="new-password"
                />
              </label>

              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">{{
                  $t('VENDOR_PORTALS.FORM.LANGUAGES')
                }}</span>
                <input
                  :value="form.supported_languages.join(', ')"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                  @input="updateLanguages($event.target.value)"
                />
              </label>

              <div class="grid grid-cols-2 gap-3">
                <label class="block">
                  <span class="mb-1 block text-sm text-n-slate-11">{{
                    $t('VENDOR_PORTALS.FORM.MARGIN')
                  }}</span>
                  <input
                    v-model.number="form.margin_rules.margin_value"
                    type="number"
                    min="0"
                    class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                  />
                </label>
                <label class="block">
                  <span class="mb-1 block text-sm text-n-slate-11">{{
                    $t('VENDOR_PORTALS.FORM.UNDERCUT')
                  }}</span>
                  <input
                    v-model.number="form.margin_rules.competitor_undercut"
                    type="number"
                    min="0"
                    class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
                  />
                </label>
              </div>

              <div class="rounded-md bg-n-alpha-2 p-3 text-sm text-n-slate-11">
                {{ $t('VENDOR_PORTALS.READ_ONLY_NOTICE') }}
              </div>

              <div class="flex gap-2">
                <NextButton
                  blue
                  type="submit"
                  icon="i-lucide-save"
                  :is-loading="isSaving"
                  :label="
                    editingId
                      ? $t('VENDOR_PORTALS.FORM.UPDATE')
                      : $t('VENDOR_PORTALS.FORM.SAVE')
                  "
                />
                <NextButton
                  v-if="editingId"
                  ghost
                  slate
                  type="button"
                  :label="$t('VENDOR_PORTALS.FORM.CANCEL')"
                  @click="cancelEdit"
                />
              </div>
            </form>
          </div>
        </aside>
      </div>
    </template>
  </SettingsLayout>
</template>
